import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';

type SocketContextProps = {
  socket: Socket | null;
  isConnected: boolean;
};

const SocketContext = createContext<SocketContextProps | null>(null);

export const useSocket = (): SocketContextProps | null => {
  return useContext(SocketContext);
};

type SocketProviderProps = {
  children: ReactNode;
};

export const SocketProvider = ({ children }: SocketProviderProps) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_URL;
    if (!socketUrl) {
      console.warn('REACT_APP_SOCKET_URL is not defined. Socket connection is disabled.');
      return undefined;
    }

    const s = io(socketUrl);

    s.on('connect', () => {
      console.debug('[SocketProvider] Socket connected', s.id);
      setIsConnected(true);
    });

    s.on('disconnect', () => {
      console.debug('[SocketProvider] Socket disconnected');
      setIsConnected(false);
    });

    s.on('connect_error', (err) => {
      toast.error('Socket connection error');
      console.error('[SocketProvider] Socket connection error', err);
      setIsConnected(false);
    });

    setSocket(s);

    return () => {
      console.debug('[SocketProvider] Disconnecting socket');
      s.disconnect();
    };
  }, []);

  return <SocketContext.Provider value={{ socket, isConnected }}>{children}</SocketContext.Provider>;
};
